import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
    X,
    Sparkles,
    Key,
    Video,
    CheckCircle2,
    ArrowRight,
    ShieldCheck,
    EyeOff
} from 'lucide-react';
import { toast } from 'react-hot-toast';

interface Day1ReadinessSplashProps {
    userId?: string | number;
}

export const Day1ReadinessSplash: React.FC<Day1ReadinessSplashProps> = ({ userId }) => {
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [dontShowAgainChecked, setDontShowAgainChecked] = useState(false);

    const permanentKey = `lgl_day1_readiness_permanent_optout_${userId || 'current'}`;
    const sessionKey = `lgl_day1_readiness_session_seen_${userId || 'current'}`;

    useEffect(() => {
        // 1. Local quick check
        const isPermanentlyOptedOut = localStorage.getItem(permanentKey) === 'true';
        if (isPermanentlyOptedOut) {
            return;
        }

        // 2. Session check (avoid re-popping in the same session)
        const isSeenInThisSession = sessionStorage.getItem(sessionKey) === 'true';
        if (isSeenInThisSession) {
            return;
        }

        // 3. Synchronize with backend cloud account
        const checkCloudDismissal = async () => {
            const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
            const token = localStorage.getItem('token');
            if (token) {
                try {
                    const res = await fetch(`${API_URL}/student/readiness-status`, {
                        headers: {
                            'Accept': 'application/json',
                            'Authorization': `Bearer ${token}`
                        }
                    });
                    if (res.ok) {
                        const data = await res.json();
                        if (data.splash_dismissed || data.checklist?.splash_dismissed) {
                            try {
                                localStorage.setItem(permanentKey, 'true');
                            } catch {}
                            return; // Permanently opted out in cloud; do not show
                        }
                    }
                } catch (e) {
                    // Ignore offline fallback
                }
            }

            // Show on this login session
            setIsOpen(true);
        };

        const timer = setTimeout(() => {
            checkCloudDismissal();
        }, 300);

        return () => clearTimeout(timer);
    }, [permanentKey, sessionKey]);

    // Lock background scrolling when full-screen splash is active
    useEffect(() => {
        if (isOpen) {
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalOverflow;
            };
        }
    }, [isOpen]);

    // Permanent dismissal (synchronized to backend database and localStorage)
    const handlePermanentDismiss = async () => {
        try {
            localStorage.setItem(permanentKey, 'true');
            sessionStorage.setItem(sessionKey, 'true');
        } catch (e) {
            // Ignore storage errors
        }
        setIsOpen(false);

        // Sync to backend cloud account
        const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
        const token = localStorage.getItem('token');
        if (token) {
            try {
                await fetch(`${API_URL}/student/readiness-status`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        splash_dismissed: true,
                        item_id: 'splash_dismissed',
                        completed: true
                    })
                });
            } catch (err) {
                // Silently ignore network failures
            }
        }

        toast.success("Preference saved across all your devices.", {
            duration: 3500,
            position: 'bottom-center'
        });
    };

    // Temporary close (only for this session; will show again on next login)
    const handleSessionDismiss = (redirectToReadiness: boolean = false) => {
        try {
            sessionStorage.setItem(sessionKey, 'true');
            if (dontShowAgainChecked) {
                handlePermanentDismiss();
                return;
            }
        } catch (e) {
            // Ignore storage errors
        }
        setIsOpen(false);

        if (redirectToReadiness) {
            navigate('/student/readiness');
        }
    };

    if (!isOpen) return null;

    // Render directly into document.body using a Portal to cover 100% of the screen (including topbar & sidebar)
    return createPortal(
        <div
            className="fixed inset-0 top-0 left-0 right-0 bottom-0 w-screen h-screen min-h-screen z-[99999] flex items-center justify-center p-4 sm:p-6 overflow-y-auto bg-slate-950/85 backdrop-blur-xl animate-fadeIn select-none"
            style={{ margin: 0, padding: '1rem' }}
        >
            {/* Ambient Background Lighting */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden flex items-center justify-center">
                <div className="w-[700px] h-[700px] bg-emerald-500/20 rounded-full blur-[140px] -translate-y-10" />
            </div>

            {/* Modal Card */}
            <div className="relative w-full max-w-2xl bg-white dark:bg-[#151d28] rounded-3xl p-6 sm:p-10 border border-slate-200/80 dark:border-slate-800 shadow-2xl overflow-hidden my-auto animate-scaleUp text-slate-900 dark:text-white">
                {/* Decorative Top Accent Banner */}
                <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-emerald-500 via-teal-400 to-amber-400" />

                {/* Close Button (Dismiss for current session) */}
                <button
                    onClick={() => handleSessionDismiss(false)}
                    aria-label="Close Announcement"
                    title="Close for this session"
                    className="absolute top-5 right-5 p-2.5 rounded-full text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                    <X size={20} />
                </button>

                <div className="space-y-6">
                    {/* Header Tag */}
                    <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1.5 px-3.5 py-1 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-full border border-emerald-500/20 text-[11px] font-black uppercase tracking-widest">
                            <Sparkles size={13} />
                            <span>Action Required Before Day One</span>
                        </div>
                    </div>

                    {/* Title & Description */}
                    <div className="space-y-2">
                        <h2 className="text-2xl sm:text-3xl font-black tracking-tight leading-tight">
                            Day 1 Class Readiness
                        </h2>
                        <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 font-medium leading-relaxed">
                            Welcome! To ensure a seamless start, your computer workstation must be prepared prior to your first class session with the required software tools, license keys, and platform verifications.
                        </p>
                    </div>

                    {/* Highlighted Core Steps */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 pt-1">
                        {/* Box 1: Software & License Keys */}
                        <div className="p-4 bg-slate-50 dark:bg-slate-900/60 rounded-2xl border border-slate-200/80 dark:border-slate-800 space-y-2 flex flex-col justify-between">
                            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0 border border-emerald-500/20">
                                <Key size={20} />
                            </div>
                            <div>
                                <h4 className="text-xs font-black uppercase tracking-wider">
                                    Software & Keys
                                </h4>
                                <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium mt-1 leading-snug">
                                    Install Primavera P6 & MSP with download links & 1-click MSP license (Mac requires Parallels Desktop).
                                </p>
                            </div>
                        </div>

                        {/* Box 2: Teams Collaboration */}
                        <div className="p-4 bg-slate-50 dark:bg-slate-900/60 rounded-2xl border border-slate-200/80 dark:border-slate-800 space-y-2 flex flex-col justify-between">
                            <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-500 flex items-center justify-center shrink-0 border border-blue-500/20">
                                <Video size={20} />
                            </div>
                            <div>
                                <h4 className="text-xs font-black uppercase tracking-wider">
                                    Teams Setup
                                </h4>
                                <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium mt-1 leading-snug">
                                    Access Microsoft Teams for live classes, meetings, and cohort channels.
                                </p>
                            </div>
                        </div>

                        {/* Box 3: Preparation Checklist */}
                        <div className="p-4 bg-slate-50 dark:bg-slate-900/60 rounded-2xl border border-slate-200/80 dark:border-slate-800 space-y-2 flex flex-col justify-between">
                            <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center shrink-0 border border-amber-500/20">
                                <CheckCircle2 size={20} />
                            </div>
                            <div>
                                <h4 className="text-xs font-black uppercase tracking-wider">
                                    Cloud Checklist
                                </h4>
                                <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium mt-1 leading-snug">
                                    Mark off each item as you prepare; saved automatically to your account.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Reassurance Banner */}
                    <div className="p-3.5 bg-emerald-500/5 dark:bg-emerald-500/10 rounded-2xl border border-emerald-500/20 flex items-center gap-3 text-xs text-slate-700 dark:text-slate-200">
                        <ShieldCheck size={18} className="text-emerald-500 shrink-0" />
                        <span className="leading-tight">
                            You can return to the <strong>Day 1 Class Readiness</strong> page at any time from your dashboard.
                        </span>
                    </div>

                    {/* Footer Actions & Don't Show Again Button */}
                    <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-slate-200/60 dark:border-slate-800/80 pt-4">
                        {/* Dedicated "Don't Show Again" Button */}
                        <button
                            type="button"
                            onClick={handlePermanentDismiss}
                            className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-red-500 dark:text-slate-400 dark:hover:text-red-400 transition-colors py-1.5 px-2.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800/50 self-start sm:self-auto"
                        >
                            <EyeOff size={14} />
                            <span>Don't show again</span>
                        </button>

                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 w-full sm:w-auto">
                            <button
                                type="button"
                                onClick={() => handleSessionDismiss(false)}
                                className="px-5 py-3 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-2xl text-xs font-black uppercase tracking-wider transition-all text-center border border-slate-200 dark:border-slate-700"
                            >
                                Continue to Dashboard
                            </button>

                            <button
                                type="button"
                                onClick={() => handleSessionDismiss(true)}
                                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-2xl text-xs font-black uppercase tracking-wider transition-all shadow-lg shadow-emerald-600/30 hover:scale-[1.02] active:scale-[0.98] text-center"
                            >
                                <span>Get Day 1 Ready Now</span>
                                <ArrowRight size={15} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};
